import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { HPC_COLORS } from "./hpcTheme";
import { formatLkr } from "./hpcMembershipPricing";
import {
  getPackageDisabledReason,
  isPackageEligible,
  STUDENT_ELIGIBILITY_LINES,
  STUDENT_TRAINING_HOURS_LINES,
} from "./hpcFormConfig";

const selectedSx = {
  borderColor: HPC_COLORS.hpuOrange,
  boxShadow: `0 0 0 2px ${HPC_COLORS.glow}`,
  bgcolor: "rgba(245, 166, 35, 0.08)",
};

const isPackageDisabled = (pkg, memberAge) => !isPackageEligible(pkg.id, memberAge);

// Price columns shared by the adult, student and couple tables and mobile cards (all from the API).
const PRICE_FIELDS = [
  { key: "standardValue", label: "Standard price", render: (pkg) => formatLkr(pkg.standardValue) },
  { key: "admissionFee", label: "Admission", render: (pkg) => `+ ${formatLkr(pkg.admissionFee)}` },
  { key: "totalFee", label: "Offer price", render: (pkg) => formatLkr(pkg.totalFee), bold: true },
  {
    key: "saving",
    label: "Saving",
    render: (pkg) => (pkg.saving > 0 ? formatLkr(pkg.saving) : "—"),
    color: (pkg) => (pkg.saving > 0 ? HPC_COLORS.tebaBlue : "text.secondary"),
  },
];

// Compact cells keep amounts and terms on one line without horizontal scroll in the 720px form.
const tableCellSx = { px: 1, whiteSpace: "nowrap" };

const FoundingCard = ({ pkg, selected, onSelect, disabled, disabledReason }) => {
  const soldOut = !pkg.available;
  const isDisabled = soldOut || disabled;

  return (
    <Paper
      variant="outlined"
      component="button"
      type="button"
      disabled={isDisabled}
      onClick={() => !isDisabled && onSelect(pkg)}
      sx={{
        p: 2.5,
        height: "100%",
        width: "100%",
        textAlign: "left",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.55 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
        borderLeft: `4px solid ${isDisabled ? "rgba(255,255,255,0.15)" : HPC_COLORS.hpuOrange}`,
        ...(selected && !isDisabled ? selectedSx : {}),
        "&:hover": isDisabled
          ? {}
          : {
              borderColor: HPC_COLORS.hpuOrange,
              boxShadow: `0 0 0 1px ${HPC_COLORS.glow}`,
            },
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ color: HPC_COLORS.hpuOrange, lineHeight: 1.4 }}>
            {pkg.label}
          </Typography>
          {soldOut ? (
            <Chip label="Sold out" size="small" color="default" />
          ) : disabled ? (
            <Chip label="Not eligible" size="small" color="default" />
          ) : selected ? (
            <CheckCircleOutlineIcon sx={{ color: HPC_COLORS.hpuOrange, fontSize: 22 }} />
          ) : null}
        </Box>

        {pkg.subtitle && (
          <Typography variant="body2" color="text.secondary">
            {pkg.subtitle}
          </Typography>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Total upfront
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: HPC_COLORS.white }}>
            {formatLkr(pkg.totalFee)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pkg.admissionFee
              ? `(includes ${formatLkr(pkg.admissionFee)} admission fee)`
              : "(No admission fee)"}
          </Typography>
        </Box>

        {pkg.saving > 0 && (
          <Typography variant="body2" sx={{ color: HPC_COLORS.tebaBlue, fontWeight: 600 }}>
            Saves {formatLkr(pkg.saving)}
          </Typography>
        )}

        {(pkg.benefits || []).map((benefit) => (
          <Stack key={benefit} direction="row" spacing={1} alignItems="flex-start">
            <StarOutlineIcon sx={{ fontSize: 16, color: HPC_COLORS.hpuOrange, mt: 0.25 }} />
            <Typography variant="body2">{benefit}</Typography>
          </Stack>
        ))}

        <Typography variant="caption" sx={{ color: HPC_COLORS.silver, fontWeight: 600 }}>
          {soldOut
            ? "No slots remaining"
            : disabled
              ? disabledReason
              : `Limited: ${pkg.remaining} of ${pkg.limit} remaining`}
        </Typography>

        {!isDisabled && (
          <Button
            variant={selected ? "contained" : "outlined"}
            size="small"
            sx={{ alignSelf: "flex-start", mt: 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(pkg);
            }}
          >
            {selected ? "Selected" : "Select package"}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

const CommitmentRowCard = ({ pkg, selected, onSelect, disabled, disabledReason }) => (
  <Paper
    variant="outlined"
    component="button"
    type="button"
    disabled={disabled}
    onClick={() => !disabled && onSelect(pkg)}
    sx={{
      p: 2,
      mb: 1.5,
      width: "100%",
      textAlign: "left",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      transition: "border-color 0.2s, box-shadow 0.2s",
      ...(selected && !disabled ? selectedSx : {}),
      "&:hover": disabled
        ? {}
        : {
            borderColor: HPC_COLORS.hpuOrange,
            boxShadow: `0 0 0 1px ${HPC_COLORS.glow}`,
          },
    }}
  >
    <Stack spacing={1}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {pkg.label}
        </Typography>
        {disabled ? (
          <Chip label="Not eligible" size="small" />
        ) : selected ? (
          <CheckCircleOutlineIcon sx={{ color: HPC_COLORS.hpuOrange, fontSize: 20 }} />
        ) : null}
      </Box>
      {disabled && disabledReason && (
        <Typography variant="caption" color="text.secondary">
          {disabledReason}
        </Typography>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        {PRICE_FIELDS.map((field) => (
          <Box key={field.key}>
            <Typography variant="caption" color="text.secondary">
              {field.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                ...(field.bold ? { fontWeight: 700 } : {}),
                ...(field.color ? { color: field.color(pkg) } : {}),
              }}
            >
              {field.render(pkg)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  </Paper>
);

const CommitmentTable = ({ packages, selectedId, onSelect, memberAge }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ width: "100%" }}>
      <TableHead>
        <TableRow>
          {["Term", ...PRICE_FIELDS.map((field) => field.label), ""].map((col) => (
            <TableCell
              key={col || "action"}
              sx={{
                ...tableCellSx,
                // Headers may wrap; amounts and terms in the rows never do.
                whiteSpace: "normal",
                fontWeight: 700,
                color: HPC_COLORS.silver,
                borderBottom: `1px solid rgba(245, 166, 35, 0.2)`,
              }}
            >
              {col}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {packages.map((pkg) => {
          const selected = selectedId === pkg.id;
          const disabled = isPackageDisabled(pkg, memberAge);
          return (
            <TableRow
              key={pkg.id}
              hover={!disabled}
              selected={selected}
              sx={{
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                "&.Mui-selected": { bgcolor: "rgba(245, 166, 35, 0.08) !important" },
                "&:hover": disabled ? {} : { bgcolor: "rgba(245, 166, 35, 0.05)" },
              }}
              onClick={() => !disabled && onSelect(pkg)}
            >
              <TableCell sx={{ ...tableCellSx, fontWeight: 600 }}>{pkg.label}</TableCell>
              {PRICE_FIELDS.map((field) => (
                <TableCell
                  key={field.key}
                  sx={{
                    ...tableCellSx,
                    ...(field.bold ? { fontWeight: 700 } : {}),
                    ...(field.color ? { color: field.color(pkg) } : {}),
                  }}
                >
                  {field.render(pkg)}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ px: 1 }}>
                {disabled ? (
                  <Typography variant="caption" color="text.secondary">
                    {getPackageDisabledReason(pkg.id, memberAge)}
                  </Typography>
                ) : (
                  <Button
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(pkg);
                    }}
                  >
                    {selected ? "Selected" : "Select"}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const StudentInfoPanel = () => (
  <Alert severity="info" sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      Student eligibility
    </Typography>
    {STUDENT_ELIGIBILITY_LINES.map((line) => (
      <Typography key={line} variant="body2" sx={{ mb: 0.25 }}>
        {line}
      </Typography>
    ))}
    <Box sx={{ mt: 1 }}>
      {STUDENT_TRAINING_HOURS_LINES.map((line) => (
        <Typography key={line} variant="body2">
          {line}
        </Typography>
      ))}
    </Box>
  </Alert>
);

const CommitmentSection = ({
  title,
  subtitle,
  admissionNote,
  packages,
  selectedId,
  onSelect,
  isMobile,
  memberAge,
}) => {
  if (!packages.length) return null;
  const admissionFee = packages[0].admissionFee;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: HPC_COLORS.silver }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1.5, color: HPC_COLORS.tebaBlue }}>
        Admission fee {formatLkr(admissionFee)} {admissionNote}. The offer price includes admission.
      </Typography>
      {isMobile ? (
        packages.map((pkg) => {
          const disabled = isPackageDisabled(pkg, memberAge);
          return (
            <CommitmentRowCard
              key={pkg.id}
              pkg={pkg}
              selected={selectedId === pkg.id}
              onSelect={onSelect}
              disabled={disabled}
              disabledReason={getPackageDisabledReason(pkg.id, memberAge)}
            />
          );
        })
      ) : (
        <CommitmentTable
          packages={packages}
          selectedId={selectedId}
          onSelect={onSelect}
          memberAge={memberAge}
        />
      )}
    </Box>
  );
};

const positionLabel = (position) => {
  if (position === 1) return "1st";
  if (position === 2) return "2nd";
  if (position === 3) return "3rd";
  return `${position}th`;
};

const discountText = (discounts = []) =>
  discounts
    .map(({ position, discountPercent }, index) => {
      const last = index === discounts.length - 1;
      const who = last ? `${positionLabel(position)} and above` : positionLabel(position);
      return `${who}: ${discountPercent ? `${discountPercent}% off` : "standard fee"}`;
    })
    .join(" · ");

const FamilySection = ({ packages, selectedId, onSelect, isMobile }) => {
  if (!packages.length) return null;
  const first = packages[0];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: HPC_COLORS.silver }}>
        Family Membership Packages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Register your whole family together. Members aged {first.adultMinAge}+ pay the adult price,
        under {first.adultMinAge} pay the student price, with a discount by position in the family.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5, color: HPC_COLORS.tebaBlue }}>
        {discountText(first.positionDiscounts)}. Admission fee {formatLkr(first.admissionFee)} per
        member.
      </Typography>
      {isMobile ? (
        packages.map((pkg) => {
          const selected = selectedId === pkg.id;
          return (
            <Paper
              key={pkg.id}
              variant="outlined"
              component="button"
              type="button"
              onClick={() => onSelect(pkg)}
              sx={{
                p: 2,
                mb: 1.5,
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                ...(selected ? selectedSx : {}),
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {pkg.label}
                </Typography>
                {selected && (
                  <CheckCircleOutlineIcon sx={{ color: HPC_COLORS.hpuOrange, fontSize: 20 }} />
                )}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Adult ({pkg.adultMinAge}+)
                  </Typography>
                  <Typography variant="body2">{formatLkr(pkg.adultFee)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Student (under {pkg.adultMinAge})
                  </Typography>
                  <Typography variant="body2">{formatLkr(pkg.studentFee)}</Typography>
                </Box>
              </Box>
            </Paper>
          );
        })
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 520 }}>
            <TableHead>
              <TableRow>
                {[
                  "Term",
                  `Adult (${first.adultMinAge}+)`,
                  `Student (under ${first.adultMinAge})`,
                  "Admission (per member)",
                  "",
                ].map((col) => (
                  <TableCell
                    key={col || "action"}
                    sx={{
                      fontWeight: 700,
                      color: HPC_COLORS.silver,
                      borderBottom: `1px solid rgba(245, 166, 35, 0.2)`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => {
                const selected = selectedId === pkg.id;
                return (
                  <TableRow
                    key={pkg.id}
                    hover
                    selected={selected}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": { bgcolor: "rgba(245, 166, 35, 0.08) !important" },
                    }}
                    onClick={() => onSelect(pkg)}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{pkg.label}</TableCell>
                    <TableCell>{formatLkr(pkg.adultFee)}</TableCell>
                    <TableCell>{formatLkr(pkg.studentFee)}</TableCell>
                    <TableCell>+ {formatLkr(pkg.admissionFee)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={selected ? "contained" : "outlined"}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(pkg);
                        }}
                      >
                        {selected ? "Selected" : "Select"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const MembershipPackagePicker = ({
  foundingAdult = [],
  foundingStudent = [],
  commitmentAdult = [],
  commitmentStudent = [],
  coupleAdult = [],
  family = [],
  selectedId,
  onSelect,
  loading,
  error,
  onRetry,
  memberAge,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  if (loading) {
    return (
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={180} />
        <Skeleton variant="rounded" height={180} />
        <Skeleton variant="rounded" height={120} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
        <Button variant="outlined" size="small" onClick={onRetry}>
          Retry
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a membership package. Fees on the payment step will be set automatically.
      </Typography>

      {foundingAdult.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: HPC_COLORS.silver }}>
            Adult Founding Member
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {foundingAdult.map((pkg) => {
              const disabled = isPackageDisabled(pkg, memberAge);
              return (
                <Grid item xs={12} md={6} key={pkg.id}>
                  <FoundingCard
                    pkg={pkg}
                    selected={selectedId === pkg.id}
                    onSelect={onSelect}
                    disabled={disabled}
                    disabledReason={getPackageDisabledReason(pkg.id, memberAge)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {foundingStudent.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: HPC_COLORS.silver }}>
            Student Founding Member
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {foundingStudent.map((pkg) => {
              const disabled = isPackageDisabled(pkg, memberAge);
              return (
                <Grid item xs={12} md={6} key={pkg.id}>
                  <FoundingCard
                    pkg={pkg}
                    selected={selectedId === pkg.id}
                    onSelect={onSelect}
                    disabled={disabled}
                    disabledReason={getPackageDisabledReason(pkg.id, memberAge)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <CommitmentSection
        title="Adult Membership Packages"
        admissionNote="per member"
        packages={commitmentAdult}
        selectedId={selectedId}
        onSelect={onSelect}
        isMobile={isMobile}
        memberAge={memberAge}
      />

      <CommitmentSection
        title="Couple Membership Packages"
        subtitle="Couple package pricing for two people registering together."
        admissionNote="for the couple. One admission fee for both members"
        packages={coupleAdult}
        selectedId={selectedId}
        onSelect={onSelect}
        isMobile={isMobile}
        memberAge={memberAge}
      />

      <FamilySection
        packages={family}
        selectedId={selectedId}
        onSelect={onSelect}
        isMobile={isMobile}
      />

      {(commitmentStudent.length > 0 || foundingStudent.length > 0) && <StudentInfoPanel />}

      <CommitmentSection
        title="Student Membership Packages"
        subtitle="Student access during designated student training hours only."
        admissionNote="per member"
        packages={commitmentStudent}

        selectedId={selectedId}
        onSelect={onSelect}
        isMobile={isMobile}
        memberAge={memberAge}
      />
    </Box>
  );
};

export default MembershipPackagePicker;
